import React from "react";
import { motion } from "framer-motion";
import {
  Target,
  Crosshair,
  Flag,
  Footprints,
  Sparkles,
  Gift,
  UtensilsCrossed,
  Beer,
  Baby,
} from "lucide-react";
import { FOOD_IMAGE_URL } from "../lib/utils";

const ADULTS = [
  { icon: Target, billes: "100", price: "22" },
  { icon: Crosshair, billes: "200", price: "27" },
  { icon: Flag, billes: "300", price: "32" },
  { icon: Footprints, billes: "500", price: "37" },
];

export default function Pricing() {
  return (
    <section
      id="tarifs"
      data-testid="pricing-section"
      className="relative bg-[#fafafa] py-20 md:py-28 lg:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63]">
                03 — Forfaits &amp; Tarifs
              </p>
              <span
                data-testid="new-prices-badge"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FFEB3B] text-black font-mono text-[10px] font-bold uppercase tracking-[0.18em] border-2 border-black"
              >
                <Sparkles className="w-3 h-3" strokeWidth={3} />
                Nouveaux tarifs
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
              Du <span className="text-[#E91E63]">paintball pas cher</span>
              <br />
              pour tous les groupes
            </h2>
          </div>
          <p className="text-base md:text-lg text-gray-700 max-w-md">
            Adultes dès 12 ans et juniors de 6 à 12 ans. Tarifs clairs, sans surprise. Réserver paintball 35 n&apos;a jamais été aussi simple.
          </p>
        </div>

        {/* Adults */}
        <div className="mb-12">
          <div className="flex items-baseline justify-between mb-6">
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
              Forfaits Adultes
              <span className="ml-3 text-sm font-mono text-gray-500 font-normal">
                (à partir de 12 ans)
              </span>
            </h3>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black border-2 border-black">
            {ADULTS.map((p, i) => (
              <motion.div
                key={p.billes}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`pricing-adult-${p.billes}`}
                className="relative bg-white p-6 md:p-8 group hover:bg-[#E91E63] transition-colors duration-300"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#00E676] group-hover:bg-[#FFEB3B] transition-colors" />
                <p.icon
                  className="w-8 h-8 md:w-10 md:h-10 text-[#E91E63] group-hover:text-white mb-6 transition-colors"
                  strokeWidth={2}
                />
                <p className="font-display text-3xl md:text-4xl font-bold uppercase text-black group-hover:text-white transition-colors">
                  {p.billes}
                  <span className="block text-xs font-mono tracking-[0.3em] mt-1 text-gray-500 group-hover:text-white/80">
                    billes
                  </span>
                </p>
                <p className="mt-6 font-display text-5xl md:text-6xl font-bold text-black group-hover:text-white transition-colors">
                  {p.price}€
                  <span className="block text-xs font-mono font-normal tracking-wider mt-1 text-gray-500 group-hover:text-white/80">
                    / pers.
                  </span>
                </p>
              </motion.div>
            ))}
          </div>

          <p className="mt-4 text-sm text-gray-600 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#E91E63]" strokeWidth={2.5} />
            <span>
              <strong>Recharge possible</strong> pour 6€ les 100 billes — pour prolonger le fun.
            </span>
          </p>
        </div>

        {/* Junior - new tier */}
        <div className="mb-12">
          <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mb-6">
            Tarif Junior
            <span className="ml-3 text-sm font-mono text-gray-500 font-normal">
              (de 6 à 12 ans)
            </span>
          </h3>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            data-testid="pricing-junior-block"
            className="relative border-2 border-black bg-white p-6 md:p-10 lg:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 overflow-hidden"
          >
            <div className="absolute -right-8 -top-8 w-40 h-40 bg-[#FFEB3B] rounded-full opacity-90 -z-0" />
            <div className="relative flex items-start gap-5 flex-1">
              <div className="shrink-0 p-3 bg-[#E91E63] text-white">
                <Baby className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2} />
              </div>
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black text-[#FFEB3B] font-mono text-[10px] font-bold uppercase tracking-[0.2em] mb-3">
                  ⭐ Nouveau
                </div>
                <p className="font-display text-2xl md:text-3xl font-bold uppercase leading-tight">
                  Une partie au tarif Junior
                </p>
                <p className="text-sm md:text-base text-gray-700 mt-2 max-w-md">
                  Un tarif unique et flexible : payez à la partie selon le rythme de votre enfant.
                  Idéal pour anniversaires et centres aérés.
                </p>
              </div>
            </div>
            <div className="relative text-right shrink-0">
              <p className="font-display text-6xl md:text-7xl font-bold leading-none">
                5€
              </p>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-gray-600 mt-1">
                par partie / joueur
              </p>
            </div>
          </motion.div>
        </div>

        {/* Food & Drinks option */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          data-testid="food-option-block"
          className="mb-12 grid md:grid-cols-12 border-2 border-black overflow-hidden"
        >
          <div className="md:col-span-7 bg-white p-6 md:p-10 lg:p-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00E676] text-black font-mono text-[10px] font-bold uppercase tracking-[0.2em] mb-5 border-2 border-black">
              <UtensilsCrossed className="w-3.5 h-3.5" strokeWidth={3} />
              Option restauration
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold uppercase leading-tight tracking-tight">
              Nourriture &amp;<br />
              <span className="text-[#E91E63]">Boissons</span>
            </h3>
            <p className="mt-4 text-base text-gray-700 max-w-md">
              En option : <strong>galettes-saucisses et boissons</strong> disponibles sur réservation uniquement (à prévoir avant la séance).
              Parfait pour prolonger votre événement après la session !
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-3 py-2 border-2 border-gray-200 text-sm font-semibold">
                <UtensilsCrossed className="w-4 h-4 text-[#E91E63]" strokeWidth={2.5} />
                Galettes-saucisses
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-2 border-2 border-gray-200 text-sm font-semibold">
                <Beer className="w-4 h-4 text-[#E91E63]" strokeWidth={2.5} />
                Boissons
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-2 border-2 border-[#E91E63] bg-[#fce4ec] text-[#E91E63] text-sm font-bold">
                À réserver à l&apos;avance
              </span>
            </div>
          </div>
          <div className="md:col-span-5 relative aspect-[4/3] md:aspect-auto min-h-[240px] bg-black">
            <img
              src={FOOD_IMAGE_URL}
              alt="Galettes-saucisses et boissons — option restauration Paintball Aventures 35"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        </motion.div>

        {/* Gift Cards block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          data-testid="gift-card-block"
          className="relative grid md:grid-cols-2 gap-0 border-2 border-black overflow-hidden"
        >
          <div className="bg-[#E91E63] text-white p-8 md:p-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFEB3B] text-black font-mono text-xs font-bold uppercase tracking-[0.2em] mb-6 border-2 border-black">
              <Gift className="w-3.5 h-3.5" strokeWidth={3} />
              Nouveauté CE
            </div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold uppercase leading-none">
              Cartes Cadeaux
            </h3>
            <p className="mt-4 text-white/90 text-base md:text-lg max-w-md">
              Disponibles pour les entreprises qui souhaitent faire plaisir ou fêter l&apos;anniversaire de leurs employés. Valables sur nos 2 sites : Fougères &amp; Louvigné-du-Désert.
            </p>
            <a
              href="tel:+33668220160"
              data-testid="gift-card-cta"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-white text-[#E91E63] font-bold uppercase text-sm tracking-wider hover:-translate-y-0.5 hover:shadow-[0_8px_0_-2px_#0a0a0a] transition-all"
            >
              Commander une carte
            </a>
          </div>
          <div className="relative aspect-[4/3] md:aspect-auto bg-black">
            <img
              src="https://customer-assets.emergentagent.com/job_2ea096a8-5204-489c-9894-ec27d46c88f0/artifacts/ok4rxubk_IMG_1441.PNG"
              alt="Cartes cadeaux Paintball Aventures 35 — idée cadeau team building entreprise Fougères"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
